import { useMemo, useState } from 'react'
import { useQuery } from 'react-query'
import Card, {
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from '../components/UI/Card'
import Button from '../components/UI/Button'
import LoadingSpinner from '../components/UI/LoadingSpinner'
import { FileText, Download, Filter, Building2, DollarSign, TrendingUp, Wallet, Eye } from 'lucide-react'
import { format, subMonths } from 'date-fns'
import { jsPDF } from 'jspdf'
import api from '../services/api'
import { paymentsAPI } from '../services/api'
import { organizationsAPI } from '../services/organizationsAPI'
import InvoiceDetailModal from '../components/Modals/InvoiceDetailModal'
import InvoiceSummaryModal from '../components/Modals/InvoiceSummaryModal'

const statusColors = {
  paid: 'bg-green-100 text-green-800',
  pending: 'bg-yellow-100 text-yellow-800',
  failed: 'bg-red-100 text-red-800',
}

const formatCurrency = (value) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(value || 0)

const formatDate = (value) => {
  if (!value) return 'N/A'
  try {
    return format(new Date(value), 'MMM dd, yyyy')
  } catch {
    return 'Invalid date'
  }
}

// Transform payment data to invoice format
const transformPaymentToInvoice = (payment) => {
  const paymentDate = payment.payment_date || payment.created_at
  const issueDate = paymentDate ? new Date(paymentDate) : new Date()
      const dueDate = new Date(issueDate)
      dueDate.setDate(dueDate.getDate() + 30)

  return {
    id: payment.id,
    invoiceNumber: payment.transaction_id || `INV-${payment.id.substring(0, 8).toUpperCase()}`,
        coverage: `${format(issueDate, 'MMM yyyy')} Coverage`,
    productName: payment.product_name || 'N/A',
    amount: parseFloat(payment.amount || 0),
        issueDate: issueDate.toISOString(),
        dueDate: dueDate.toISOString(),
    status: payment.payment_status === 'completed' ? 'paid' : 
            payment.payment_status === 'failed' ? 'failed' : 'pending',
    billingCycle: (payment.billing_cycle || 'Monthly').charAt(0).toUpperCase() + (payment.billing_cycle || 'Monthly').slice(1),
    seats: payment.user_limit ? `${payment.user_limit} seats` : 'Unlimited seats',
    organization_id: payment.organization_id,
    product_id: payment.product_id,
    plan_name: payment.plan_name
  }
}

const filterOrganizationsByPeriod = (organizations, period) => {
  if (period === 'all') return organizations

  const now = new Date()
  let cutoff = new Date(now)

  switch (period) {
    case 'monthly':
      cutoff = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate())
      break
    case '6months':
      cutoff = new Date(now.getFullYear(), now.getMonth() - 6, now.getDate())
      break
    case '1year':
      cutoff = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate())
      break
    default:
      break
  }

  return organizations
    .map((org) => ({
      ...org,
      invoices: org.invoices.filter((invoice) => new Date(invoice.issueDate) >= cutoff),
    }))
    .filter((org) => org.invoices.length > 0)
}

const periodLabels = {
  all: 'All Time',
  monthly: 'Last Month',
  '6months': 'Last 6 Months',
  '1year': 'Last Year',
}

// PDF Generation Function
const generatePDF = (org, invoice) => {
  try {
    if (typeof jsPDF === 'undefined') {
      throw new Error('jsPDF library is not loaded. Please ensure jspdf is installed.')
    }

    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    })
    const pageWidth = doc.internal.pageSize.getWidth()
    const margin = 20
    let yPos = margin

    // Top Section - Company Name (Left) and INVOICE Title (Right)
    doc.setFontSize(24)
    doc.setFont('helvetica', 'bold')
    doc.text(org.name || 'N/A', margin, yPos)
    
    // INVOICE title on the right
    doc.setFontSize(28)
    doc.text('INVOICE', pageWidth - margin, yPos, { align: 'right' })
    yPos += 8

    // Email below company name
    doc.setFontSize(12)
    doc.setFont('helvetica', 'normal')
    doc.text(org.email || 'N/A', margin, yPos)
    yPos += 10

    // Invoice # and Date on the left
    doc.setFontSize(11)
    doc.text(`Invoice #: ${invoice.invoiceNumber}`, margin, yPos)
    yPos += 6
    doc.text(`Date: ${formatDate(invoice.issueDate)}`, margin, yPos)
    yPos += 15

    // Table Header
    const tableStartY = yPos
    const col1X = margin
    const col2X = margin + 60
    const col3X = pageWidth - margin - 30
    
    // Table header background (light gray)
    doc.setFillColor(240, 240, 240)
    doc.rect(col1X, tableStartY - 6, pageWidth - (margin * 2), 8, 'F')
    
    // Table header text
    doc.setFontSize(11)
    doc.setFont('helvetica', 'bold')
    doc.text('Product Name', col1X, tableStartY)
    doc.text('Billing Period', col2X, tableStartY)
    doc.text('Amount', col3X, tableStartY, { align: 'right' })
    yPos = tableStartY + 8

    // Table row
    doc.setFont('helvetica', 'normal')
    const productName = invoice.productName || 'N/A'
    const billingPeriod = invoice.plan_name ? `${invoice.plan_name} - ${invoice.billingCycle} Plan` : `${invoice.billingCycle} Plan`
    
    // Row background (white)
    doc.setFillColor(255, 255, 255)
    doc.rect(col1X, yPos - 6, pageWidth - (margin * 2), 8, 'F')
    
    // Row border
    doc.setDrawColor(200, 200, 200)
    doc.setLineWidth(0.1)
    doc.line(col1X, yPos - 6, pageWidth - margin, yPos - 6)
    doc.line(col1X, yPos + 2, pageWidth - margin, yPos + 2)
    
    doc.text(productName, col1X, yPos)
    doc.text(billingPeriod, col2X, yPos)
    doc.text(formatCurrency(invoice.amount), col3X, yPos, { align: 'right' })
    yPos += 15

    // Status, Created Date, and Due Date
    doc.setFontSize(10)
    const status = invoice.status === 'paid' ? 'Paid' : 
                   invoice.status === 'pending' ? 'Pending' : 
                   invoice.status === 'failed' ? 'Failed' : 'Pending'
    
    doc.text(`Status: ${status}`, margin, yPos)
    yPos += 6
    doc.text(`Created Date: ${formatDate(invoice.issueDate)}`, margin, yPos)
    yPos += 6
    doc.text(`Due Date: ${formatDate(invoice.dueDate)}`, margin, yPos)

    // Save PDF
    const fileName = `Invoice-${invoice.invoiceNumber}.pdf`
    doc.save(fileName)
  } catch (error) {
    console.error('Error generating PDF:', error)
    alert('Failed to generate PDF. Please check the browser console for details.')
  }
}

const handleDownload = (org, invoice) => {
  console.log('Downloading PDF for invoice:', invoice.invoiceNumber)
  try {
    generatePDF(org, invoice)
  } catch (error) {
    console.error('Error in handleDownload:', error)
    alert('An error occurred while downloading the invoice. Please try again.')
  }
}

const handleDownloadSummary = (org, invoices, periodLabel) => {
  try {
    if (typeof jsPDF === 'undefined') {
      throw new Error('jsPDF library is not loaded. Please ensure jspdf is installed.')
    }

    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    })
    const pageWidth = doc.internal.pageSize.getWidth()
    const margin = 20
    let yPos = margin

    const totalAmount = invoices.reduce((sum, inv) => sum + inv.amount, 0)
    const paidCount = invoices.filter((inv) => inv.status === 'paid').length
    const pendingCount = invoices.filter((inv) => inv.status === 'pending').length
    const failedCount = invoices.filter((inv) => inv.status === 'failed').length

    // Top Section - Company Name (Left) and Summary Title (Right)
    doc.setFontSize(24)
    doc.setFont('helvetica', 'bold')
    doc.text(org.name || 'N/A', margin, yPos)
    
    // Summary title on the right
    doc.setFontSize(20)
    doc.text(`${periodLabel} Summary`, pageWidth - margin, yPos, { align: 'right' })
    yPos += 8

    // Email below company name
    doc.setFontSize(12)
    doc.setFont('helvetica', 'normal')
    doc.text(org.email || 'N/A', margin, yPos)
    yPos += 10

    // Summary Information
    doc.setFontSize(11)
    doc.text(`Invoices: ${invoices.length}`, margin, yPos)
    yPos += 6
    doc.text(`Total Amount: ${formatCurrency(totalAmount)}`, margin, yPos)
    yPos += 6
    doc.text(`Paid: ${paidCount} · Pending: ${pendingCount} · Failed: ${failedCount}`, margin, yPos)
    yPos += 15

    // Invoice Breakdown Section Title
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(14)
    doc.text('Invoice Breakdown', margin, yPos)
    yPos += 10

    // Invoice Breakdown Table Header
    const tableStartY = yPos
    const col1X = margin
    const col2X = margin + 12
    const col3X = margin + 50
    const col4X = margin + 85
    // Keep amount and status close to the right edge to give
    // the Product column more room and avoid overlap.
    const col6X = pageWidth - margin
    const col5X = col6X - 30
    
    // Table header background (light gray)
    doc.setFillColor(240, 240, 240)
    doc.rect(col1X, tableStartY - 6, pageWidth - (margin * 2), 8, 'F')
    
    // Table header text
    doc.setFontSize(10)
    doc.setFont('helvetica', 'bold')
    doc.text('#', col1X, tableStartY)
    doc.text('Invoice #', col2X, tableStartY)
    doc.text('Date', col3X, tableStartY)
    doc.text('Product', col4X, tableStartY)
    doc.text('Amount', col5X, tableStartY, { align: 'right' })
    doc.text('Status', col6X, tableStartY, { align: 'right' })
    yPos = tableStartY + 8

    // Invoice rows
    doc.setFont('helvetica', 'normal')
    invoices.forEach((invoice, index) => {
      // Check if we need a new page
      if (yPos > doc.internal.pageSize.getHeight() - 20) {
        doc.addPage()
        yPos = margin
        
        // Redraw header on new page
        doc.setFillColor(240, 240, 240)
        doc.rect(col1X, yPos - 6, pageWidth - (margin * 2), 8, 'F')
        doc.setFont('helvetica', 'bold')
        doc.setFontSize(10)
        doc.text('#', col1X, yPos)
        doc.text('Invoice #', col2X, yPos)
        doc.text('Date', col3X, yPos)
        doc.text('Product', col4X, yPos)
        doc.text('Amount', col5X, yPos, { align: 'right' })
        doc.text('Status', col6X, yPos, { align: 'right' })
        yPos += 8
        doc.setFont('helvetica', 'normal')
      }
      
      // Row background (white)
      doc.setFillColor(255, 255, 255)
      doc.rect(col1X, yPos - 6, pageWidth - (margin * 2), 8, 'F')
      
      // Row border
      doc.setDrawColor(200, 200, 200)
      doc.setLineWidth(0.1)
      doc.line(col1X, yPos - 6, pageWidth - margin, yPos - 6)
      doc.line(col1X, yPos + 2, pageWidth - margin, yPos + 2)
      
      doc.text(`${index + 1}.`, col1X, yPos)
      doc.text(invoice.invoiceNumber, col2X, yPos)
      doc.text(formatDate(invoice.issueDate), col3X, yPos)

      // Truncate product name based on available width so it
      // doesn't collide with the Amount column in the PDF.
      const productName = invoice.productName || 'N/A'
      const availableProductWidth = col5X - col4X - 4
      let productText = productName
      while (doc.getTextWidth(productText) > availableProductWidth && productText.length > 3) {
        productText = productText.slice(0, -4) + '...'
      }
      doc.text(productText, col4X, yPos)
      
      doc.text(formatCurrency(invoice.amount), col5X, yPos, { align: 'right' })
      doc.text(invoice.status.toUpperCase(), col6X, yPos, { align: 'right' })
      yPos += 8
    })

    // Footer
    doc.setFontSize(10)
    doc.setFont('helvetica', 'normal')
    doc.text(`Generated on ${format(new Date(), 'MMM dd, yyyy')}`, pageWidth / 2, doc.internal.pageSize.getHeight() - 10, {
      align: 'center',
    })

    const safePeriodLabel = periodLabel.toLowerCase().replace(/\s+/g, '-')
    doc.save(`${org.name}-${safePeriodLabel}-summary.pdf`)
  } catch (error) {
    console.error('Error generating summary PDF:', error)
    alert('Failed to generate summary PDF. Please try again.')
  }
}

export default function Invoice() {
  const [selectedPeriod, setSelectedPeriod] = useState('all')
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null)
  const [selectedOrgId, setSelectedOrgId] = useState(null)
  const [showInvoiceModal, setShowInvoiceModal] = useState(false)
  const [showSummaryModal, setShowSummaryModal] = useState(false)
  const [summaryData, setSummaryData] = useState(null)

  // Fetch all organizations
  const { data: organizationsData, isLoading: orgsLoading } = useQuery(
    ['organizations', 'all'],
    () => organizationsAPI.getAll({ limit: 1000, status: 'active' }),
    {
      onError: (error) => {
        console.error('Error fetching organizations:', error)
      }
    }
  )

  const allOrganizations = organizationsData?.data?.data?.organizations || []

  // Fetch payments for all organizations
  const paymentResults = useQuery(
    ['all-payments', allOrganizations.map(o => o.id).join(',')],
    async () => {
      if (allOrganizations.length === 0) return []
      
      const results = await Promise.allSettled(
        allOrganizations.map(org => 
          paymentsAPI.getByOrganization(org.id)
            .then(r => ({ organizationId: org.id, payments: r.data?.data?.payments || [] }))
            .catch(() => ({ organizationId: org.id, payments: [] }))
        )
      )
      return results.map((result) => 
        result.status === 'fulfilled' ? result.value : { organizationId: '', payments: [] }
      )
    },
    {
      enabled: allOrganizations.length > 0,
      onError: (error) => {
        console.error('Error fetching payments:', error)
      }
    }
  )

  // Transform data to match expected format
  const organizations = useMemo(() => {
    if (!paymentResults.data || allOrganizations.length === 0) return []

    return allOrganizations.map(org => {
      const orgPayments = paymentResults.data.find(p => p.organizationId === org.id)?.payments || []
      const invoices = orgPayments.map(transformPaymentToInvoice)

      return {
        id: org.id,
        name: org.name,
        email: org.email,
        location: org.address || org.location || null,
        invoices
      }
    }).filter(org => org.invoices.length > 0) // Only show organizations with invoices
  }, [paymentResults.data, allOrganizations])

  const isLoading = orgsLoading || paymentResults.isLoading
  const filteredOrganizations = useMemo(() => filterOrganizationsByPeriod(organizations, selectedPeriod), [organizations, selectedPeriod])

  const allInvoices = filteredOrganizations.flatMap((org) => org.invoices)
  const totalAmount = allInvoices.reduce((sum, invoice) => sum + invoice.amount, 0)
  const totalPaid = allInvoices.filter((inv) => inv.status === 'paid').reduce((sum, inv) => sum + inv.amount, 0)
  const totalPending = allInvoices.filter((inv) => inv.status !== 'paid').reduce((sum, inv) => sum + inv.amount, 0)

  const metricData = useMemo(() => {
    const now = new Date()
    const currentMonthKey = format(now, 'yyyy-MM')
    const currentMonthInvoices = allInvoices.filter((inv) => {
      try {
        return format(new Date(inv.issueDate), 'yyyy-MM') === currentMonthKey
      } catch {
        return false
      }
    })
    const currentMonthAmount = currentMonthInvoices.reduce((sum, inv) => sum + inv.amount, 0)
    const lastMonthKey = format(new Date(now.getFullYear(), now.getMonth() - 1, 1), 'yyyy-MM')
    const lastMonthAmount = allInvoices
      .filter((inv) => {
        try {
          return format(new Date(inv.issueDate), 'yyyy-MM') === lastMonthKey
        } catch {
          return false
        }
      })
      .reduce((sum, inv) => sum + inv.amount, 0)

    const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate()
    const forecast =
      currentMonthAmount > 0 ? (currentMonthAmount / Math.max(now.getDate(), 1)) * daysInMonth : currentMonthAmount

    const costChange = lastMonthAmount
      ? (((currentMonthAmount - lastMonthAmount) / lastMonthAmount) * 100).toFixed(1)
      : 0

    return {
      currentMonthAmount,
      forecast,
      totalPaid,
      costChange: parseFloat(costChange),
    }
  }, [allInvoices, totalPaid])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Invoices</h1>
          <p className="text-gray-600 mt-1">Review invoices for each organization.</p>
        </div>
      </div>

      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 flex-1">
          {[
            {
              label: 'Current Month',
              value: formatCurrency(metricData.currentMonthAmount),
              sub: `${metricData.costChange >= 0 ? '+' : ''}${metricData.costChange}% from last month`,
              iconBg: 'bg-blue-100 text-blue-600',
              icon: DollarSign,
            },
            {
              label: 'Forecasted',
              value: formatCurrency(metricData.forecast),
              sub: 'This month',
              iconBg: 'bg-green-100 text-green-600',
              icon: TrendingUp,
            },
            {
              label: 'Total Paid',
              value: formatCurrency(metricData.totalPaid),
              sub: 'All time',
              iconBg: 'bg-purple-100 text-purple-600',
              icon: Wallet,
            },
            {
              label: 'Invoices',
              value: allInvoices.length,
              sub: `${allInvoices.filter((inv) => inv.status === 'paid').length} paid`,
              iconBg: 'bg-orange-100 text-orange-600',
              icon: FileText,
            },
          ].map((metric, index) => {
            const Icon = metric.icon
            return (
              <Card key={index} className="border border-gray-200 shadow-sm rounded-2xl">
                <CardContent className="p-5 flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">{metric.label}</p>
                    <p className="text-2xl font-semibold text-gray-900 mt-1">{metric.value}</p>
                    <p className="text-xs text-gray-500 mt-1">{metric.sub}</p>
                  </div>
                  <div className={`h-12 w-12 rounded-xl flex items-center justify-center ${metric.iconBg}`}>
                    <Icon className="h-5 w-5" />
                  </div>
                </CardContent>
              </Card>
            )
          })}
        </div>
        <div className="flex items-center gap-3 self-end lg:self-auto">
          <label className="flex items-center text-sm font-medium text-gray-700 gap-2 whitespace-nowrap">
            <Filter className="h-4 w-4 text-gray-500" />
            Period
          </label>
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Time</option>
            <option value="monthly">Last Month</option>
            <option value="6months">Last 6 Months</option>
            <option value="1year">Last Year</option>
          </select>
        </div>
      </div>

      {filteredOrganizations.length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-12">
              <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No invoices available for the selected period.</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {filteredOrganizations.map((org) => {
            const invoices = org.invoices
            const hasInvoices = invoices.length > 0
            const periodLabel = periodLabels[selectedPeriod] || 'Selected Period'
            const totalAmount = hasInvoices ? invoices.reduce((sum, inv) => sum + inv.amount, 0) : 0
            const paidCount = hasInvoices ? invoices.filter((inv) => inv.status === 'paid').length : 0
            const pendingCount = hasInvoices ? invoices.filter((inv) => inv.status === 'pending').length : 0
            const failsCount = hasInvoices ? invoices.filter((inv) => inv.status === 'failed').length : 0
            const earliestDate = hasInvoices
              ? invoices.reduce((earliest, inv) => {
                  const invDate = new Date(inv.issueDate)
                  return invDate < new Date(earliest) ? inv.issueDate : earliest
                }, invoices[0].issueDate)
              : null
            const latestDate = hasInvoices
              ? invoices.reduce((latest, inv) => {
                  const invDate = new Date(inv.issueDate)
                  return invDate > new Date(latest) ? inv.issueDate : latest
                }, invoices[0].issueDate)
              : null
            const dateRange = hasInvoices
              ? invoices.length > 1
                ? `${formatDate(earliestDate)} - ${formatDate(latestDate)}`
                : formatDate(invoices[0].issueDate)
              : 'N/A'
            const shouldAggregateInvoices = hasInvoices
            const uniqueProducts = hasInvoices ? [...new Set(invoices.map((inv) => inv.productName))] : []
            const uniqueStatuses = hasInvoices ? [...new Set(invoices.map((inv) => inv.status))] : []
            const aggregatedRow = shouldAggregateInvoices
              ? {
                  invoiceNumber: `${periodLabel} Total`,
                  coverage: dateRange,
                  productName:
                    uniqueProducts.length === 1
                      ? uniqueProducts[0]
                      : `${uniqueProducts.length} Products`,
                  issueDate: dateRange,
                  amount: totalAmount,
                  statusLabel:
                    uniqueStatuses.length === 1
                      ? uniqueStatuses[0].charAt(0).toUpperCase() + uniqueStatuses[0].slice(1)
                      : 'Mixed',
                  statusColor:
                    uniqueStatuses.length === 1
                      ? statusColors[uniqueStatuses[0]] || statusColors.pending
                      : 'bg-gray-100 text-gray-800',
                  actionLabel: `${invoices.length} invoices`,
                }
              : null

            return (
              <Card key={org.id}>
                <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
                      <Building2 className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <CardTitle className="text-xl">{org.name}</CardTitle>
                      <CardDescription>
                        {org.email}
                        {org.location ? ` · ${org.location}` : ''}
                      </CardDescription>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-500">Invoices</p>
                    <p className="text-2xl font-bold text-gray-900">{org.invoices.length}</p>
                    <p className="text-sm text-gray-600">
                      {formatCurrency(org.invoices.reduce((sum, inv) => sum + inv.amount, 0))}
                    </p>
                  </div>
                </div>
              </CardHeader>
                <CardContent className="space-y-4">
                  {!hasInvoices ? (
                    <div className="text-center py-8 text-gray-500">No invoices available for this period.</div>
                  ) : (
                    <div className="rounded-xl border border-gray-200 p-4 space-y-6">
                      <div className="flex flex-wrap items-center gap-3">
                        <div className="flex-1">
                          <p className="text-lg font-semibold text-gray-900">{periodLabel} Summary</p>
                        </div>
                        <span className="text-xs font-medium px-3 py-1 rounded-full bg-blue-100 text-blue-800">
                          {periodLabel}
                        </span>
                      </div>

                      <div className="grid gap-4 md:grid-cols-4">
                        <div>
                          <p className="text-xs uppercase text-gray-500">Total Amount</p>
                          <p className="text-base font-semibold text-gray-900">{formatCurrency(totalAmount)}</p>
                        </div>
                        <div>
                          <p className="text-xs uppercase text-gray-500">Paid</p>
                          <p className="text-base font-semibold text-gray-900">{paidCount}</p>
                        </div>
                        <div>
                          <p className="text-xs uppercase text-gray-500">Pending / Failed</p>
                          <p className="text-base font-semibold text-gray-900">
                            {pendingCount} pending · {failsCount} failed
                          </p>
                        </div>
                        <div>
                          <p className="text-xs uppercase text-gray-500">Date Range</p>
                          <p className="text-base font-semibold text-gray-900">{dateRange}</p>
                        </div>
                      </div>

                      {/* Invoice Breakdown Table */}
                      <div className="rounded-lg border border-gray-200 overflow-hidden">
                        <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                          <p className="text-sm font-semibold text-gray-900">Invoice Breakdown</p>
                        </div>
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invoice #</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Coverage</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Issue Date</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Action</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                              {shouldAggregateInvoices && aggregatedRow ? (
                                <tr className="bg-blue-50/50">
                                  <td className="px-4 py-3 text-sm font-semibold text-gray-900">
                                    {aggregatedRow.invoiceNumber}
                                  </td>
                                  <td className="px-4 py-3 text-sm text-gray-600">{aggregatedRow.coverage}</td>
                                  <td className="px-4 py-3 text-sm text-gray-600">{aggregatedRow.productName}</td>
                                  <td className="px-4 py-3 text-sm text-gray-600">{aggregatedRow.issueDate}</td>
                                  <td className="px-4 py-3 text-sm font-semibold text-gray-900">
                                    {formatCurrency(aggregatedRow.amount)}
                                  </td>
                                  <td className="px-4 py-3">
                                    <span
                                      className={`text-xs font-medium px-2 py-1 rounded-full ${aggregatedRow.statusColor}`}
                                    >
                                      {aggregatedRow.statusLabel}
                                    </span>
                                  </td>
                                  <td className="px-4 py-3 text-right">
                                    <div className="flex items-center justify-end space-x-2">
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => {
                                          setSummaryData({
                                            organization: org,
                                            invoices: invoices,
                                            periodLabel: periodLabel
                                          })
                                          setShowSummaryModal(true)
                                        }}
                                        className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                        title="View Summary"
                                      >
                                        <Eye className="h-4 w-4" />
                                      </Button>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handleDownloadSummary(org, invoices, periodLabel)}
                                        className="text-gray-600 hover:text-gray-700 hover:bg-gray-50"
                                        title="Download Summary"
                                      >
                                        <Download className="h-4 w-4" />
                                      </Button>
                                    </div>
                                  </td>
                                </tr>
                              ) : null}
                              {/* Individual Invoice Rows */}
                              {invoices.map((invoice) => (
                                <tr key={invoice.id} className="hover:bg-gray-50">
                                  <td className="px-4 py-3 text-sm text-gray-900">
                                    {invoice.invoiceNumber}
                                  </td>
                                  <td className="px-4 py-3 text-sm text-gray-600">{invoice.coverage}</td>
                                  <td className="px-4 py-3 text-sm text-gray-600">{invoice.productName}</td>
                                  <td className="px-4 py-3 text-sm text-gray-600">{formatDate(invoice.issueDate)}</td>
                                  <td className="px-4 py-3 text-sm font-semibold text-gray-900">
                                    {formatCurrency(invoice.amount)}
                                  </td>
                                  <td className="px-4 py-3">
                                    <span
                                      className={`text-xs font-medium px-2 py-1 rounded-full ${
                                        invoice.status === 'paid' 
                                          ? statusColors.paid
                                          : invoice.status === 'pending'
                                          ? statusColors.pending
                                          : statusColors.failed
                                      }`}
                                    >
                                      {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                                    </span>
                                  </td>
                                  <td className="px-4 py-3 text-right">
                                    <div className="flex items-center justify-end space-x-2">
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => {
                                          setSelectedInvoiceId(invoice.id)
                                          setSelectedOrgId(org.id)
                                          setShowInvoiceModal(true)
                                        }}
                                        className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                        title="View Invoice"
                                      >
                                        <Eye className="h-4 w-4" />
                                      </Button>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handleDownload(org, invoice)}
                                        className="text-gray-600 hover:text-gray-700 hover:bg-gray-50"
                                        title="Download Invoice"
                                      >
                                        <Download className="h-4 w-4" />
                                      </Button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )
          })}
        </div>
      )}

      {/* Invoice Detail Modal */}
      <InvoiceDetailModal
        isOpen={showInvoiceModal}
        onClose={() => {
          setShowInvoiceModal(false)
          setSelectedInvoiceId(null)
          setSelectedOrgId(null)
        }}
        paymentId={selectedInvoiceId}
        organizationId={selectedOrgId}
        onDownload={(payment) => {
          // Find the organization
          const org = filteredOrganizations.find(o => o.id === selectedOrgId)
          if (org && payment) {
            const invoice = transformPaymentToInvoice(payment)
            handleDownload(org, invoice)
          }
        }}
      />

      {/* Invoice Summary Modal */}
      {summaryData && (
        <InvoiceSummaryModal
          isOpen={showSummaryModal}
          onClose={() => {
            setShowSummaryModal(false)
            setSummaryData(null)
          }}
          organization={summaryData.organization}
          invoices={summaryData.invoices}
          periodLabel={summaryData.periodLabel}
          onViewInvoice={(invoiceId, orgId) => {
            setShowSummaryModal(false)
            setSelectedInvoiceId(invoiceId)
            setSelectedOrgId(orgId)
            setShowInvoiceModal(true)
          }}
          onDownloadInvoice={(org, invoice) => {
            handleDownload(org, invoice)
          }}
        />
      )}
    </div>
  )
}

